/**
 * Persisted shape of a scatter plot's fit settings, kept React-free so the
 * validation and clamping rules can be reasoned about (and tested) on their own.
 */

/** `window: null` means "untouched — track the full data range". */
export interface StoredFit {
  showFit: boolean;
  window: { low: number; high: number } | null;
  manualOffsetOn: boolean;
  manualOffset: number;
}

export const FIT_DEFAULTS: StoredFit = {
  showFit: false,
  window: null,
  manualOffsetOn: false,
  manualOffset: 0,
};

/**
 * Validate whatever localStorage handed back. Returns undefined for input that
 * isn't a record at all (so the caller falls back wholesale); otherwise each
 * field independently falls back to its default, which is what lets an older
 * stored shape keep working instead of discarding the whole record.
 */
export function reviveFit(raw: unknown): StoredFit | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const stored = raw as Record<string, unknown>;
  const win = stored.window as Record<string, unknown> | null | undefined;
  const low = win?.low;
  const high = win?.high;
  return {
    showFit: stored.showFit === true,
    // A window is only usable if both bounds survived and still bracket correctly.
    window: typeof low === 'number' && Number.isFinite(low)
      && typeof high === 'number' && Number.isFinite(high) && high >= low
      ? { low, high }
      : null,
    manualOffsetOn: stored.manualOffsetOn === true,
    manualOffset: typeof stored.manualOffset === 'number' && Number.isFinite(stored.manualOffset)
      ? stored.manualOffset
      : 0,
  };
}

/**
 * Resolve a stored window against the loaded run's range. A window carried over
 * from a run with a wider x-range is pinned to this run's maximum rather than
 * stranding the plot past the end of the slider; an untouched window spans the
 * full range. Clamping happens here, on read — never written back, so the stored
 * preference still applies in full to the next run wide enough for it.
 *
 * Clamping both bounds with the same ceiling keeps `low <= high` intact.
 */
export function clampFitWindow(
  stored: StoredFit['window'],
  maxX: number,
): { low: number; high: number } {
  return {
    low: Math.min(stored?.low ?? 0, maxX),
    high: Math.min(stored?.high ?? maxX, maxX),
  };
}
